import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck } from "lucide-react";
import { API } from "@/lib/api";
import { useConsent } from "@/context/ConsentContext";
import TipwinLogo from "@/components/TipwinLogo";

export default function Footer() {
  const [locations, setLocations] = useState([]);
  const { resetConsent } = useConsent();

  useEffect(() => {
    API.get("/locations")
      .then((res) => setLocations(res.data))
      .catch(() => setLocations([]));
  }, []);

  return (
    <footer className="border-t border-[#27272A] bg-[#0E0E10] pb-[env(safe-area-inset-bottom,0px)]" data-testid="site-footer" data-snap="start">
      <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-12">
        <div className="grid grid-cols-1 gap-10 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <div className="flex items-center gap-3">
              <span className="font-display text-lg font-extrabold uppercase tracking-tight text-[#F5F5F3]">
                Sportwetten<span className="text-[#F54855]">&nbsp;NRW</span>
              </span>
              <TipwinLogo className="h-5 w-auto" />
            </div>
            <p className="mt-4 text-sm leading-relaxed text-[#A1A1AA]">
              Lokale Tipwin-Wettvermittlungsstellen in Nordrhein-Westfalen. Live-Sport, persönlicher
              Service und Lounge-Atmosphäre vor Ort.
            </p>
          </div>

          <nav aria-label="Standorte im Footer">
            <h2 className="text-xs font-semibold uppercase tracking-[0.2em] text-[#F5F5F3]">Standorte</h2>
            <ul className="mt-4 space-y-2">
              {locations.map((loc) => (
                <li key={loc.slug}>
                  <Link
                    to={`/standorte/${loc.slug}`}
                    className="text-sm text-[#A1A1AA] transition-colors hover:text-[#F5F5F3]"
                    data-testid={`footer-location-${loc.slug}`}
                  >
                    {loc.name}
                  </Link>
                </li>
              ))}
              <li>
                <Link to="/standorte" className="text-sm font-medium text-[#F54855] transition-colors hover:text-[#F54855]" data-testid="footer-link-alle-standorte">
                  Alle Standorte
                </Link>
              </li>
            </ul>
          </nav>

          <nav aria-label="Rechtliches im Footer">
            <h2 className="text-xs font-semibold uppercase tracking-[0.2em] text-[#F5F5F3]">Rechtliches</h2>
            <ul className="mt-4 space-y-2">
              <li>
                <Link to="/impressum" className="text-sm text-[#A1A1AA] transition-colors hover:text-[#F5F5F3]" data-testid="footer-link-impressum">
                  Impressum
                </Link>
              </li>
              <li>
                <Link to="/datenschutz" className="text-sm text-[#A1A1AA] transition-colors hover:text-[#F5F5F3]" data-testid="footer-link-datenschutz">
                  Datenschutz
                </Link>
              </li>
              <li>
                <Link to="/spielerschutz" className="text-sm text-[#A1A1AA] transition-colors hover:text-[#F5F5F3]" data-testid="footer-link-spielerschutz">
                  Spielerschutz
                </Link>
              </li>
              <li>
                <button
                  type="button"
                  onClick={resetConsent}
                  className="inline-flex min-h-[44px] items-center text-sm text-[#A1A1AA] transition-colors hover:text-[#F5F5F3]"
                  data-testid="footer-cookie-settings"
                >
                  Cookie-Einstellungen
                </button>
              </li>
            </ul>
          </nav>

          <div>
            <h2 className="text-xs font-semibold uppercase tracking-[0.2em] text-[#F5F5F3]">Anschrift</h2>
            <address className="mt-4 text-sm not-italic leading-relaxed text-[#A1A1AA]">
              Sportwetten NRW GmbH
              <br />
              Westerwieher Straße 1<br />
              33397 Rietberg
            </address>
          </div>
        </div>

        <div className="mt-12 flex flex-col gap-4 rounded-xl border border-[#27272A] bg-[#16161A] p-5 sm:flex-row sm:items-center" data-testid="footer-compliance">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border-2 border-[#E11D2A] font-display text-sm font-extrabold text-[#F54855]" aria-label="Ab 18 Jahren">
            18+
          </span>
          <p className="text-sm leading-relaxed text-[#A1A1AA]">
            Glücksspiel kann süchtig machen. Teilnahme erst ab 18 Jahren. Kostenlose und vertrauliche
            Beratung:{" "}
            <a href="tel:08001372700" className="inline-flex min-h-[44px] items-center font-medium text-[#F5F5F3] underline-offset-4 hover:underline" data-testid="footer-helpline">
              BZgA 0800&nbsp;1372700
            </a>{" "}
            ·{" "}
            <a href="https://www.check-dein-spiel.de" target="_blank" rel="noopener noreferrer" className="font-medium text-[#F5F5F3] underline-offset-4 hover:underline" data-testid="footer-check-dein-spiel">
              check-dein-spiel.de
            </a>
          </p>
          <ShieldCheck className="hidden h-6 w-6 shrink-0 text-[#F54855] sm:block" aria-hidden="true" />
        </div>

        <div className="mt-8 flex flex-col gap-2 border-t border-[#27272A] pt-6 text-xs text-[#96908B] sm:flex-row sm:items-center sm:justify-between">
          <p>© {new Date().getFullYear()} Sportwetten NRW GmbH. Alle Rechte vorbehalten.</p>
          <p>Wettvermittlungsstellen der Marke Tipwin · OASIS-angeschlossen · Aufsicht: Bezirksregierungen Detmold & Münster</p>
        </div>
      </div>
    </footer>
  );
}
